import { List as ImmutableList } from 'immutable';
import { DateTime, Interval } from 'luxon';
import { createSelector } from 'reselect';

import { constructPredefinedInterval } from '../../utils/date';
import { accountsSelectors } from '../accounts';
import { authSelectors } from '../auth';
import { categoriesSelectors } from '../categories';
import { selectorsHelpers } from '../core';
import { featureFlagsSelectors } from '../feature-flags';
import { preferencesSelectors } from '../preferences';
import { reportsSelectors, reportsUtils } from '../reports';
import { tagsSelectors } from '../tags';
import { transactionListUtils } from '../transaction-list';
import { transactionsSelectors } from '../transactions';

import { SPENDING_WATCH_LIST_REDUCER_KEY } from './spendingWatchlistTypes';
import { separateFutureAndPastTxns, sortWatchList } from './spendingWatchlistUtils';

export const getSpendingWatchListStore = (state) => state[SPENDING_WATCH_LIST_REDUCER_KEY];

export const getIsLoading = (state) => getSpendingWatchListStore(state).isLoading;
export const getLoadPending = (state) => getSpendingWatchListStore(state).loadPending;
export const getLastSyncDate = (state) => getSpendingWatchListStore(state).lastSyncDate;

export const getSpendingWatchLists = (state) => getSpendingWatchListStore(state).resourcesById;
export const getSpendingWatchListById = (state, id) => getSpendingWatchListStore(state).resourcesById.get(id);

export const getSpendingWatchListData = createSelector(
  (state) => getSpendingWatchLists(state),
  (spendingWatchLists) => {
    let data = ImmutableList();
    if (!spendingWatchLists) {
      return data;
    }
    let index = 0;
    spendingWatchLists.forEach((swl) => {
      data = data.push({
        id: swl.id,
        filterId: swl.filterId,
        name: swl.name,
        thisMonth: swl.thisMonth,
        yearToDate: swl.yearToDate,
        monthlyTrend: swl.monthlyTrend,
        numMonthlyTrendMonths: swl.numMonthlyTrendMonths,
        monthSpendingProjection: swl.monthSpendingProjection,
        targetAmount: swl.targetAmount,
        emoji: swl.emoji,
        createdAt: swl.createdAt,
        period: swl.period,
        startDate: swl.startDate,
        endDate: swl.endDate,
        spentSoFar: swl.spentSoFar,
        order: index,
      });
      index += 1;
    });
    return data;
  },
);

export const getSortedWatchlists = createSelector(
  (state) => getSpendingWatchListData(state),
  (state, sortOrder) => sortOrder,
  (spendingWatchLists, sortOrder) => sortWatchList(spendingWatchLists, sortOrder) || ImmutableList(),
);

export const getWatchlistDataForBarChart = createSelector(
  (state) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state) ||
    tagsSelectors.getLoadPending(state),
  (state, props) =>
    reportsSelectors.getReportFilteredTxns(state, {
      ...props,
      upcomingTxnsIncluded: true,
      reportSpecificFilter: reportsUtils.getReportSpecificFilterFromReportName(props.reportName),
    }),
  (state) => categoriesSelectors.getCategoriesById(state),
  (state) => tagsSelectors.getTags(state),
  (_, props) => props.reportName,
  (_, props) => props.timeStep,
  (_, props) => props.chartType,
  (_, props) => props.filterType,
  (_, props) => reportsUtils.getReportSpecificFilterFromReportName(props.reportName),
  (state, props) => transactionListUtils.createFilterFromFilterObject(props.filterObject),
  (state, props) => getSpendingWatchListById(state, props.id),
  (
    loadPending,
    txnsList,
    categoriesById,
    tagsById,
    reportName,
    timeStep,
    chartType,
    filterType,
    reportSpecificFilter,
    filterObjectFn,
    watchlist,
  ) => {
    if (loadPending || !txnsList || !watchlist) {
      return null;
    }
    const cacheKey = selectorsHelpers.getOrCreateStringFromObject(
      'watchlist',
      {
        txnsList,
        reportName,
        timeStep,
        chartType,
        reportSpecificFilter,
        categoriesById,
        tagsById,
        filterObjectFn,
        filterType,
        watchlist: JSON.stringify(watchlist),
      },
      20,
      selectorsHelpers.memoizeWithKeyRetrieveCache,
    );
    return selectorsHelpers.memoizeWithKey(
      'watchlist-graph-data',
      () => {
        let swlDateRange =
          watchlist.period === 'FIXED'
            ? null
            : constructPredefinedInterval({ value: 'LAST_12_MONTHS' }, DateTime.local().endOf('month'));
        if (!swlDateRange) {
          // const {startDate, endDate} = watchlist;
          const startDate = DateTime.fromISO(watchlist.startDate);
          const endDate =
            DateTime.local().endOf('month') > DateTime.fromISO(watchlist.endDate)
              ? DateTime.fromISO(watchlist.endDate)
              : DateTime.local().endOf('month');
          swlDateRange = Interval.fromDateTimes(startDate, endDate);
        }
        // For this filter if it's payees we don't use the filterObject because getOverTimeData only uses the filter on splits and since
        //    splits don't have payees they automatically fit the filter
        const filterIncludingAppliedFilters = filterObjectFn
          ? (txn, cbid) =>
              reportSpecificFilter(txn, cbid) && (filterType === 'payees' || filterObjectFn([txn])?.length > 0)
          : reportSpecificFilter;
        const report = reportsSelectors.getOverTimeData(
          txnsList,
          swlDateRange,
          timeStep,
          null,
          categoriesById,
          tagsById,
          filterIncludingAppliedFilters,
        );

        const periods =
          report
            ?.get('periods')
            ?.map((period) => ({
              total: -1 * period.total,
              category: DateTime.fromISO(period.timeStamp).toFormat('LLL'),
              custom: DateTime.fromISO(period.timeStamp).toFormat('LLLL yyyy'),
            }))
            .toArray() || null;

        return periods;
      },
      cacheKey,
      20,
    );
  },
);

export const getWatchlistDataForDisplay = createSelector(
  (state) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state) ||
    tagsSelectors.getLoadPending(state),
  (state, props) =>
    reportsSelectors.getReportFilteredTxns(state, {
      ...props,
      upcomingTxnsIncluded: true,
      reportSpecificFilter: reportsUtils.getReportSpecificFilterFromReportName(props.reportName),
    }),
  (state) => categoriesSelectors.getCategoriesById(state),
  (state) => tagsSelectors.getTags(state),
  (_, props) => props.reportName,
  (_, props) => props.timeStep,
  (_, props) => props.chartType,
  (_, props) => props.groupBy,
  (_, props) => props.filterType,
  (_, props) => reportsUtils.getReportSpecificFilterFromReportName(props.reportName),
  (state, props) => transactionListUtils.createFilterFromFilterObject(props.filterObject),
  (state, props) => getSpendingWatchListById(state, props.id),
  (
    loadPending,
    txnsList,
    categoriesById,
    tagsById,
    reportName,
    timeStep,
    chartType,
    groupBy,
    filterType,
    reportSpecificFilter,
    filterObjectFn,
    watchlist,
  ) => {
    if (loadPending || !txnsList || !watchlist) {
      return null;
    }

    const cacheKey = selectorsHelpers.getOrCreateStringFromObject(
      'watchlist',
      {
        txnsList,
        reportName,
        timeStep,
        chartType,
        groupBy,
        reportSpecificFilter,
        categoriesById,
        tagsById,
        filterObjectFn,
        filterType,
        watchlist: JSON.stringify(watchlist),
      },
      6,
      selectorsHelpers.memoizeWithKeyRetrieveCache,
    );
    return selectorsHelpers.memoizeWithKey(
      'watchlist-graph-data',
      () => {
        const retData = { allTxns: [], allTxnItems: [], chartData: {} };
        let swlDateRange =
          watchlist.period === 'FIXED'
            ? null
            : constructPredefinedInterval({ value: 'LAST_12_MONTHS' }, DateTime.local().endOf('month'));
        if (!swlDateRange) {
          // const {startDate, endDate} = watchlist;
          const startDate = DateTime.fromISO(watchlist.startDate);
          const endDate =
            DateTime.local().endOf('month') > DateTime.fromISO(watchlist.endDate)
              ? DateTime.fromISO(watchlist.endDate)
              : DateTime.local().endOf('month');
          swlDateRange = Interval.fromDateTimes(startDate, endDate);
        }
        // For this filter if it's payees we don't use the filterObject because getOverTimeData only uses the filter on splits and since
        //    splits don't have payees they automatically fit the filter
        const filterIncludingAppliedFilters = filterObjectFn
          ? (txn, cbid) =>
              reportSpecificFilter(txn, cbid) && (filterType === 'payees' || filterObjectFn([txn])?.length > 0)
          : reportSpecificFilter;
        const report = reportsSelectors.getOverTimeData(
          txnsList,
          swlDateRange,
          timeStep,
          groupBy,
          categoriesById,
          tagsById,
          filterIncludingAppliedFilters,
        );
        retData.chartData.orderedFields = Object.entries(report.get('allGroups') || {})
          .map(([groupKey, value]) => ({ label: groupKey, total: value }))
          .sort((a, b) => -1 * b.total - -1 * a.total)
          .map(({ label }) => label);

        const { txnsWithoutUpcoming, txnsUpcoming } = separateFutureAndPastTxns(Array.from(report.get('allTxns')));
        const { txnsItemsWithoutUpcoming, txnsItemsUpcoming } = separateFutureAndPastTxns(
          Array.from(report.get('allTxnItems')),
        );
        retData.total = report.get('total');
        retData.allTxns = report.get('allTxns');
        retData.allTxnItems = report.get('allTxnItems');
        retData.allTxnsWithoutUpcoming = txnsWithoutUpcoming;
        retData.allTxnsUpcoming = txnsUpcoming;
        retData.allTxnsItemsWithoutUpcoming = txnsItemsWithoutUpcoming;
        retData.allTxnsItemsUpcoming = txnsItemsUpcoming;

        retData.chartData.periods =
          report
            ?.get('periods')
            ?.map((period) => {
              const miniCardDataArray = []; // Array<{amount: never, name: string, percentage: number, txns: never}>
              if (period.groups) {
                // handle groups of data
                Object.entries(period.groups).forEach(([groupKey, groupData]) => {
                  const newGroupObject = {
                    name: groupKey,
                    amount: groupData.total,
                    percentage: Number(Math.abs((groupData.total / period.total) * 100).toFixed(2)),
                    txns: groupData.txns,
                    txnItems: groupData.txnItems,
                    ...separateFutureAndPastTxns(groupData.txns),
                    ...separateFutureAndPastTxns(groupData.txnItems),
                  };
                  miniCardDataArray.push(newGroupObject);
                });
              }

              return {
                timeStamp: period.timeStamp,
                total: -1 * period.total,
                txns: period.txns,
                txnItems: period.txnItems,
                ...separateFutureAndPastTxns(period.txns),
                ...separateFutureAndPastTxns(period.txnItems),
                miniCardDataArray: miniCardDataArray.sort((a, b) => b.percentage - a.percentage),
              };
            })
            .toArray() || null;
        retData.trueChartData = retData.chartData.periods.map((per) => ({
          ...per,
          txns: undefined, // remove txns for optimization
          txnItems: undefined,
          category: DateTime.fromISO(per.timeStamp).toFormat('LLL'),
          custom: DateTime.fromISO(per.timeStamp).toFormat('LLLL yyyy'),
        }));

        retData.allGroups = Object.entries(report.get('allGroups') || {}).map(([groupKey, amount]) => {
          const { total } = amount;
          const txns = retData.chartData.periods.reduce((accum, period) => {
            const groupTxnsFromPeriod = period.miniCardDataArray.find((item) => item.name === groupKey)?.txns;
            return groupTxnsFromPeriod ? [...accum, ...groupTxnsFromPeriod] : accum;
          }, []);
          const txnItems = retData.chartData.periods.reduce((accum, period) => {
            const groupTxnsFromPeriod = period.miniCardDataArray.find((item) => item.name === groupKey)?.txnItems;
            return groupTxnsFromPeriod ? [...accum, ...groupTxnsFromPeriod] : accum;
          }, []);

          return {
            name: groupKey,
            amount: total,
            percentage: Number(Math.abs((total / report.get('total')) * 100).toFixed(2)),
            txns,
            txnItems,
            ...separateFutureAndPastTxns(txns),
            ...separateFutureAndPastTxns(txnItems),
          };
        });

        return retData;
      },
      cacheKey,
      6,
    );
  },
);

export const getShowSetup = createSelector(
  preferencesSelectors.getWatchlistSetupCompleted,
  authSelectors.getCurrentDataset,
  getSortedWatchlists,
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'watchlistSetup'),
  (watchlistSetupCompleted, currentDataset, spendingWatchListArray, watchlistSetup) => {
    if (!currentDataset) return false;
    const swlSetup = watchlistSetup
      ? DateTime.fromISO(currentDataset.createdAt).toMillis() > Number(watchlistSetup) * 1000
      : false;
    return swlSetup && spendingWatchListArray.size === 0 && !watchlistSetupCompleted;
  },
);
